import { SuppressMouseEventHandlingParams } from 'ag-grid-community';
import dayjs from 'dayjs';

import { DB_DATE_FORMAT } from 'core/constants';
import { TradeAgreementUnavailableReason, TradeAgreementUsage } from 'quotes/enums';
import { sanitizeAgreementText } from 'quotes/mappers/tradeAgreement';
import {
  ICustomerQuotePart,
  IQuotePartCostData,
  ISelectedSpaFormValue,
  ITradeAgreement,
  ITradeAgreementLine,
  ITradeAgreementsTransientLineRequest,
} from 'quotes/types';
import { formatSpaCost, isExternalPricingSourceInUse } from 'quotes/utils';

// All optional: Add Line has no row to fall back on, the Pricing Calculator no part field.
export interface ISelectSpaFormValues {
  accountsPayableId?: string;
  corporateLocation?: { externalLocationId?: string | number };
  manufacturer?: { id?: number };
  part?: { number?: string; vendorNumber?: string };
  pricingDate?: string;
  quantity?: number;
  shipmentType?: { alternateCode?: string };
  uom?: string;
}

export const selectSpaRequestFieldNames: (keyof ISelectSpaFormValues)[] = [
  'accountsPayableId',
  'corporateLocation',
  'manufacturer',
  'part',
  'pricingDate',
  'quantity',
  'shipmentType',
  'uom',
];

// The row fills only a field the form lacks; a field the form cleared stays cleared.
const formOrRow = <T>(formValue: T | undefined, rowValue: T): T =>
  undefined === formValue ? rowValue : formValue;

/**
 * The form is the truth even when editing. `isSpaSelectionAvailable` is sent only when true or
 * while the form holds a pick, which the pricing answer to a call directed at it reports unflagged.
 */
export const buildTradeAgreementsRequest = ({
  hasSelectedSpa,
  isSpaSelectionAvailable,
  quoteId,
  quotePart,
  values,
}: {
  hasSelectedSpa?: boolean;
  isSpaSelectionAvailable?: boolean | null;
  quoteId: number;
  quotePart?: ICustomerQuotePart | null;
  values?: ISelectSpaFormValues;
}): ITradeAgreementsTransientLineRequest => {
  const pricingDate = values?.pricingDate;
  const part = formOrRow(values?.part, quotePart?.partArr);
  const manufacturer = formOrRow(values?.manufacturer, { id: quotePart?.partArr?.manufacturerId });

  return {
    quoteId,
    number: part?.number || '',
    manufacturerId: manufacturer?.id,
    // As /prices: an option without a catalog number puts the vendor number in `number`.
    vendorNumber: part?.vendorNumber || undefined,
    quantity: Number(values?.quantity ?? quotePart?.quantity) || 0,
    uom: values?.uom || quotePart?.uom || '',
    accountsPayableId: formOrRow(values?.accountsPayableId, quotePart?.accountsPayableId),
    shipType: values?.shipmentType?.alternateCode,
    shippingLocationId: values?.corporateLocation?.externalLocationId || undefined,
    pricingDate: pricingDate ? dayjs(pricingDate).format(DB_DATE_FORMAT) : undefined,
    ...(quotePart?.id ? { quotePartId: quotePart.id } : {}),
    ...(true === isSpaSelectionAvailable || hasSelectedSpa ? { isSpaSelectionAvailable: 1 } : {}),
  };
};

/**
 * The response may or may not echo the quote part id, so the line is matched by position.
 */
export const getTransientLine = (lines?: ITradeAgreementLine[]): ITradeAgreementLine | null =>
  lines?.[0] ?? null;

const reasonTranslationKeys: Partial<Record<TradeAgreementUnavailableReason, string>> = {
  [TradeAgreementUnavailableReason.COST_LOCKED]: 'messageTradeAgreementsReasonCostLocked',
  [TradeAgreementUnavailableReason.DISCOUNT_LINE]: 'messageTradeAgreementsReasonDiscountLine',
  [TradeAgreementUnavailableReason.LOCKED]: 'messageTradeAgreementsReasonLocked',
  [TradeAgreementUnavailableReason.NO_ENTITY_CODE]: 'messageTradeAgreementsReasonNoEntityCode',
  [TradeAgreementUnavailableReason.NO_SELECTION_AVAILABLE]:
    'messageTradeAgreementsReasonNoSelectionAvailable',
  [TradeAgreementUnavailableReason.NO_SKU_UOM]: 'messageTradeAgreementsReasonNoSkuUom',
  [TradeAgreementUnavailableReason.NOT_ALTERNATE]: 'messageTradeAgreementsReasonNotAlternate',
  [TradeAgreementUnavailableReason.OMITTED]: 'messageTradeAgreementsReasonOmitted',
  [TradeAgreementUnavailableReason.SELECTION_UNKNOWN]:
    'messageTradeAgreementsReasonSelectionUnknown',
  [TradeAgreementUnavailableReason.SUBSTITUTED]: 'messageTradeAgreementsReasonSubstituted',
};

/**
 * A provider failure is a 200 with every line `line_error`: a failed lookup, not an empty list.
 */
export const isTradeAgreementsLineError = (line?: ITradeAgreementLine | null): boolean =>
  TradeAgreementUnavailableReason.LINE_ERROR === line?.reason;

export const withTradeAgreementsLineMessage = (
  text: string,
  line?: Pick<ITradeAgreementLine, 'message'> | null
): string => (line?.message ? `${text} ${line.message}` : text);

// Only the reasons the BE can actually return are worded.
export const getTradeAgreementReasonKey = (reason?: string | null): string | null =>
  reasonTranslationKeys[reason as TradeAgreementUnavailableReason] ?? null;

export const getTradeAgreementUnavailableKey = (
  line?: ITradeAgreementLine | null
): string | null => {
  if (!line || line.isSpaSelectionAvailable) {
    return null;
  }

  return getTradeAgreementReasonKey(line.reason);
};

// One code can come back at two versions, so the code alone is not a unique row id.
export const getTradeAgreementRowId = (
  agreement?: Pick<ITradeAgreement, 'spaCostSource' | 'spaCostSourceId'> | null
): string => `${agreement?.spaCostSource}:${agreement?.spaCostSourceId ?? ''}`;

/**
 * A pick without a version sits on the first row with its code. Null when no longer offered.
 */
export const getHeldAgreementRowId = (
  agreements: ITradeAgreement[],
  heldAgreement?: Pick<ITradeAgreement, 'spaCostSource' | 'spaCostSourceId'> | null
): string | null => {
  if (!heldAgreement?.spaCostSource) {
    return null;
  }

  const row = heldAgreement.spaCostSourceId
    ? agreements.find(
        (agreement) => getTradeAgreementRowId(agreement) === getTradeAgreementRowId(heldAgreement)
      )
    : agreements.find(({ spaCostSource }) => spaCostSource === heldAgreement.spaCostSource);

  return row ? getTradeAgreementRowId(row) : null;
};

export const getSelectedSpaNoLongerOfferedMessage = (
  selectedSpa: Pick<ISelectedSpaFormValue, 'spaCost' | 'spaCostCurrency' | 'spaCostSource'>,
  costDecimals?: number
): { key: string; values: Record<string, string> } => {
  const agreementCode = selectedSpa.spaCostSource;
  const spaCost = selectedSpa.spaCost as unknown;

  if (null == spaCost || '' === spaCost) {
    return { key: 'messageSelectedSpaNoLongerOffered', values: { agreementCode } };
  }

  return {
    key: 'messageSelectedSpaNoLongerOfferedWithCost',
    values: {
      agreementCode,
      cost: formatSpaCost(spaCost, costDecimals, selectedSpa.spaCostCurrency),
    },
  };
};

// The one held-row check, so the chip, tint, preselection and actions never disagree.
export const isHeldAgreement = (
  agreement?: Pick<ITradeAgreement, 'spaCostSource' | 'spaCostSourceId'> | null,
  heldRowId?: string | null
): boolean => !!agreement && !!heldRowId && getTradeAgreementRowId(agreement) === heldRowId;

export const findHeldAgreement = (
  agreements: ITradeAgreement[],
  heldAgreement?: Pick<ITradeAgreement, 'spaCostSource' | 'spaCostSourceId'> | null
): ITradeAgreement | null => {
  const heldRowId = getHeldAgreementRowId(agreements, heldAgreement);

  return agreements.find((agreement) => isHeldAgreement(agreement, heldRowId)) ?? null;
};

// The line's default SPA: the agreement Wesco's pricing puts on the line.
export interface IDefaultSpaAgreement {
  // The line is on that cost: no pick held and the cost source is External.
  isInUse: boolean;
  spaCostRegistrationNum: string | null;
  spaCostSource: string | null;
}

/**
 * The line's `external_spa_*`, which a pick leaves in place. Scrubbed like the listed rows, so the
 * two compare equal.
 */
export const getDefaultSpaAgreement = (
  costData?: Pick<
    IQuotePartCostData,
    'costSourceType' | 'externalSpaCostRegistrationNum' | 'externalSpaCostSource'
  > | null
): IDefaultSpaAgreement => ({
  isInUse: isExternalPricingSourceInUse(costData?.costSourceType),
  spaCostRegistrationNum: sanitizeAgreementText(costData?.externalSpaCostRegistrationNum) || null,
  spaCostSource: sanitizeAgreementText(costData?.externalSpaCostSource) || null,
});

/**
 * Code and Reg Number: the card carries no version key.
 */
export const isDefaultAgreement = (
  agreement?: Pick<ITradeAgreement, 'spaCostRegistrationNum' | 'spaCostSource'> | null,
  defaultAgreement?: Pick<IDefaultSpaAgreement, 'spaCostRegistrationNum' | 'spaCostSource'> | null
): boolean =>
  !!agreement &&
  !!defaultAgreement?.spaCostSource &&
  !!defaultAgreement?.spaCostRegistrationNum &&
  agreement.spaCostSource === defaultAgreement.spaCostSource &&
  agreement.spaCostRegistrationNum === defaultAgreement.spaCostRegistrationNum;

export interface ITradeAgreementUsageContext {
  // Set only while the line is on its default or holds a pick.
  defaultRowId: string | null;
  heldRowId: string | null;
  isDefaultInUse: boolean;
}

/**
 * The default is the first matching row, like a pick without a version.
 */
export const getTradeAgreementUsageContext = (
  agreements: ITradeAgreement[],
  selectedSpa?: Pick<ITradeAgreement, 'spaCostSource' | 'spaCostSourceId'> | null,
  defaultSpa?: IDefaultSpaAgreement | null
): ITradeAgreementUsageContext => {
  const hasSelectedSpa = !!selectedSpa?.spaCostSource;
  const isDefaultInUse = !hasSelectedSpa && !!defaultSpa?.isInUse;
  const defaultRow =
    isDefaultInUse || hasSelectedSpa
      ? agreements.find((agreement) => isDefaultAgreement(agreement, defaultSpa))
      : null;

  return {
    defaultRowId: defaultRow ? getTradeAgreementRowId(defaultRow) : null,
    heldRowId: getHeldAgreementRowId(agreements, selectedSpa),
    isDefaultInUse,
  };
};

// The pick wins: a pick of the default's own agreement is the line's pick.
export const getTradeAgreementUsage = (
  agreement?: Pick<ITradeAgreement, 'spaCostSource' | 'spaCostSourceId'> | null,
  { defaultRowId, heldRowId, isDefaultInUse }: Partial<ITradeAgreementUsageContext> = {}
): TradeAgreementUsage => {
  if (isHeldAgreement(agreement, heldRowId)) {
    return TradeAgreementUsage.IN_USE;
  }

  if (!agreement || !defaultRowId || getTradeAgreementRowId(agreement) !== defaultRowId) {
    return TradeAgreementUsage.NOT_IN_USE;
  }

  return isDefaultInUse ? TradeAgreementUsage.DEFAULT_IN_USE : TradeAgreementUsage.DEFAULT;
};

export const tradeAgreementUsageLabelKeys: Record<TradeAgreementUsage, string> = {
  [TradeAgreementUsage.DEFAULT]: 'labelSpaDefault',
  [TradeAgreementUsage.DEFAULT_IN_USE]: 'labelSpaDefaultInUse',
  [TradeAgreementUsage.IN_USE]: 'labelInUse',
  [TradeAgreementUsage.NOT_IN_USE]: 'labelNotInUse',
};

// AG-Grid selects the row before React's delegated handler runs, so in-cell buttons opt out here.
export const isButtonMouseEvent = ({ event }: Pick<SuppressMouseEventHandlingParams, 'event'>) =>
  event?.target instanceof Element && !!event.target.closest('button');
